import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import axios from "axios";
import MaterialTable from "material-table";
import { getTrans as t } from "../../Localization/translations";

import { API } from "../../backend";
import authHeader from "../../services/auth-header";
import subcompanyService from "../../services/company/subcompanyService";
import CompanySwitchService from "../../services/company/companySwitchService";
import { S } from "./styles";
import { SubscriberCheckout } from "./PlansPage";

// ── temporary frontend-only storage for "created but not yet activated"
// accounts. There's no backend model for this state yet (see write-up in
// chat) — once one exists, this whole PENDING_* block goes away in favor
// of a real GET/POST to the backend.
const PENDING_KEY = "athena_pending_accounts";

const loadPending = () => {
  try {
    const raw = localStorage.getItem(PENDING_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch (e) {
    return [];
  }
};

const savePending = (list) => {
  try {
    localStorage.setItem(PENDING_KEY, JSON.stringify(list));
  } catch (e) {
    // ignore quota/serialization errors — non-critical local cache
  }
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const fmtDate = (d) => {
  if (!d) return "—";
  const date = new Date(d);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-US", { day: "2-digit", month: "short", year: "numeric" });
};

const fmtStorage = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  const num = Number(v);
  if (Number.isNaN(num)) return String(v);
  return `${num.toFixed(2)} GB`;
};

// ── the grey detail panel (Start Date / End Date / Licenses / Data
// Storage / Document Storage) MaterialTable renders under a row once its
// built-in expand chevron is clicked. Fetched on mount, i.e. on-demand
// the first time that row is expanded, not upfront for every row. ──────
function AccountDetailPanel({ account }) {
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(true);
  const [detailError, setDetailError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setDetailLoading(true);
    setDetailError("");
    subcompanyService
      .getClientDetail(account.company_id)
      .then((res) => {
        if (cancelled) return;
        if (res.data && res.data.success === 1) {
          setDetail(res.data.data);
        } else {
          setDetailError((res.data && res.data.error) || "Failed to load details");
        }
      })
      .catch((err) => {
        if (cancelled) return;
        setDetailError(
          (err.response && err.response.data && err.response.data.error) || "Failed to load details"
        );
      })
      .finally(() => {
        if (!cancelled) setDetailLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [account.company_id]);

  if (detailLoading) {
    return (
      <div style={{ ...S.detailRowInner, background: "#fafafa" }}>
        <span style={S.subfootnote}>Loading details...</span>
      </div>
    );
  }
  if (detailError) {
    return (
      <div style={{ ...S.detailRowInner, background: "#fafafa" }}>
        <span style={{ ...S.subfootnote, color: "#dc2626" }}>{detailError}</span>
      </div>
    );
  }
  return (
    <div style={{ ...S.detailRowInner, background: "#fafafa" }}>
      <div>
        <div style={S.detailRowLabel}>Start Date</div>
        <div style={S.detailRowValue}>{fmtDate(detail && detail.start_date)}</div>
      </div>
      <div>
        <div style={S.detailRowLabel}>End Date</div>
        <div style={S.detailRowValue}>{fmtDate(detail && detail.end_date)}</div>
      </div>
      <div>
        <div style={S.detailRowLabel}>Licenses</div>
        <div style={S.detailRowValue}>
          {detail && detail.no_of_users !== null && detail.no_of_users !== undefined
            ? detail.no_of_users
            : "—"}
        </div>
      </div>
      <div>
        <div style={S.detailRowLabel}>Data Storage</div>
        <div style={S.detailRowValue}>{fmtStorage(detail && detail.consumed_all_storage)}</div>
      </div>
      <div>
        <div style={S.detailRowLabel}>Document Storage</div>
        <div style={S.detailRowValue}>{fmtStorage(detail && detail.consumed_doc_storage)}</div>
      </div>
    </div>
  );
}

const tableHeadCellStyle = { verticalAlign: "inherit", fontWeight: "bold", textAlign: "left" };
const tableBodyCellStyle = { textAlign: "left", fontSize: 12 };
const materialTableChrome = {
  boxShadow: "none",
  backgroundColor: "var(--bs-table-bg)",
  borderBottomWidth: "1px",
};
const materialTableOptionsBase = {
  headerStyle: { backgroundColor: "white", color: "#245C7F", fontSize: 12 },
  rowStyle: (rowData, index) => (index % 2 ? { backgroundColor: "#fff" } : {}),
  sorting: true,
  search: false,
  toolbar: false,
  paging: true,
  pageSize: 5,
  emptyRowsWhenPaging: false,
  pageSizeOptions: [5, 10, 15, 25],
  paginationType: "stepped",
  showFirstLastPageButtons: false,
  actionsColumnIndex: -1,
};

function AccountsPage() {
  const navigate = useNavigate();

  // ── real, activated companies ──────────────────────────────────────────
  const [accounts, setAccounts] = useState([]);
  const [accountsLoading, setAccountsLoading] = useState(false);

  // ── archived companies ───────────────────────────────────────────────────
  const [archivedAccounts, setArchivedAccounts] = useState([]);
  const [archivedLoading, setArchivedLoading] = useState(false);

  // ── pending (frontend-only) accounts ───────────────────────────────────
  const [pendingAccounts, setPendingAccounts] = useState(loadPending());

  // ── "new account" form ──────────────────────────────────────────────────
  const [showNewAccountForm, setShowNewAccountForm] = useState(false);
  const [newAccountForm, setNewAccountForm] = useState({
    companyName: "",
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
  });
  const [newAccountErrors, setNewAccountErrors] = useState({});
  const [savingNewAccount, setSavingNewAccount] = useState(false);

  // ── activation (Activate -> the same Subscriber's Details + Your Plan
  // checkout the Plans tab uses, just pre-filled from the pending account) ──
  const [activatingAccount, setActivatingAccount] = useState(null);

  // ── switch active company ────────────────────────────────────────────────
  const [switchingId, setSwitchingId] = useState(null);

  // ── "new sub-company" form: a company with no email / login of its own,
  // opened by switching from this account. Has its own subscription. ──────
  const todayIso = () => new Date().toISOString().slice(0, 10);
  const emptySubCompanyForm = () => ({
    companyName: "",
    contactName: "",
    phone: "",
    address: "",
    city: "",
    plan: "",
    noOfUsers: 1,
    totalAmount: "",
    startDate: todayIso(),
    subscriptionType: "CASH",
    billToParent: true,
  });
  const [showSubCompanyForm, setShowSubCompanyForm] = useState(false);
  const [subCompanyForm, setSubCompanyForm] = useState(emptySubCompanyForm());
  const [subCompanyErrors, setSubCompanyErrors] = useState({});
  const [savingSubCompany, setSavingSubCompany] = useState(false);
  const [plans, setPlans] = useState([]);

  const notify = (fn, msg) =>
    fn(msg, { position: "top-right", autoClose: 2500, theme: "light" });

  const handleSwitchCompany = async (companyId) => {
    setSwitchingId(companyId);
    try {
      const res = await CompanySwitchService.switchCompany(companyId);
      if (res.success === 1) {
        window.location.href = "/subscription";
      } else {
        notify(toast.error, res.error || "Failed to switch company");
        setSwitchingId(null);
      }
    } catch (err) {
      notify(
        toast.error,
        (err.response && err.response.data && err.response.data.error) ||
        "Failed to switch company"
      );
      setSwitchingId(null);
    }
  };

  const fetchAccounts = () => {
    setAccountsLoading(true);
    Promise.allSettled([
      subcompanyService.getMyClients(),
      axios.get(API + "company/profile/", { headers: authHeader() }),
    ])
      .then(([accountsResult, profileResult]) => {
        if (accountsResult.status !== "fulfilled") {
          notify(toast.error, "Failed to fetch accounts");
          setAccounts([]);
          return;
        }

        const list = accountsResult.value.data.clients || [];
        const profile =
          profileResult.status === "fulfilled" ? profileResult.value.data : null;
        const alreadyListed =
          profile &&
          list.some(
            (a) => a.relationship_type === "self" || a.company_id === profile.id
          );

        // The company you're actually logged into right now — the backend's
        // /company/my-clients/ only lists managed child companies, so when
        // you're at the root/top of your own company it never appears there
        // on its own. Add it here so "your own account" is always visible,
        // the same way it already is when you've switched into a child.
        // (If the /company/profile/ call itself failed, we just skip this —
        // the managed-companies list from /my-clients/ still renders fine.)
        if (!alreadyListed && profile && profile.id != null) {
          list.unshift({
            company_id: profile.id,
            company_name: profile.company_name,
            contact_name: profile.name,
            currency: profile.currency,
            plan: profile.plan,
            relationship_type: "self",
          });
        }

        setAccounts(list);
      })
      .finally(() => setAccountsLoading(false));
  };

  const fetchArchivedAccounts = () => {
    setArchivedLoading(true);
    subcompanyService
      .getArchivedClients()
      .then((res) => {
        setArchivedAccounts((res.data && res.data.clients) || []);
      })
      .catch(() => {
        setArchivedAccounts([]);
      })
      .finally(() => setArchivedLoading(false));
  };

  useEffect(() => {
    fetchAccounts();
    fetchArchivedAccounts();
  }, []);

  const handleArchive = async (account) => {
    try {
      const res = await subcompanyService.archiveClient(account.company_id);
      if (res.data && res.data.success === 1) {
        notify(toast.success, `${account.company_name} archived.`);
        fetchAccounts();
        fetchArchivedAccounts();
      } else {
        notify(toast.error, (res.data && res.data.error) || "Failed to archive account");
      }
    } catch (err) {
      notify(
        toast.error,
        (err.response && err.response.data && err.response.data.error) || "Failed to archive account"
      );
    }
  };

  const handleReinstate = async (account) => {
    try {
      const res = await subcompanyService.reinstateClient(account.company_id);
      if (res.data && res.data.success === 1) {
        notify(toast.success, `${account.company_name} reinstated.`);
        fetchAccounts();
        fetchArchivedAccounts();
      } else {
        notify(toast.error, (res.data && res.data.error) || "Failed to reinstate account");
      }
    } catch (err) {
      notify(
        toast.error,
        (err.response && err.response.data && err.response.data.error) || "Failed to reinstate account"
      );
    }
  };

  // ── new account form ────────────────────────────────────────────────────
  const resetNewAccountForm = () => {
    setNewAccountForm({ companyName: "", firstName: "", lastName: "", email: "", phone: "" });
    setNewAccountErrors({});
  };

  const validateNewAccount = () => {
    const errors = {};
    if (!newAccountForm.companyName.trim()) errors.companyName = "Account name is required";
    if (!newAccountForm.firstName.trim()) errors.firstName = "First name is required";
    if (!newAccountForm.lastName.trim()) errors.lastName = "Last name is required";
    if (!newAccountForm.email.trim()) {
      errors.email = "Email is required";
    } else if (!EMAIL_RE.test(newAccountForm.email.trim())) {
      errors.email = "Enter a valid email address";
    }
    return errors;
  };

  const handleNewAccountFieldChange = (field) => (e) => {
    const value = e.target.value;
    setNewAccountForm((prev) => ({ ...prev, [field]: value }));
    if (newAccountErrors[field]) {
      setNewAccountErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const handleCreateAccountRecord = async (e) => {
    e.preventDefault();
    const errors = validateNewAccount();
    setNewAccountErrors(errors);
    if (Object.keys(errors).length > 0) {
      notify(toast.error, "Please fix the highlighted fields");
      return;
    }

    setSavingNewAccount(true);
    try {
      // Real check against the same endpoint the rest of the checkout uses,
      // so we don't let someone create a pending account with an email
      // that's already registered (which would fail later at activation).
      const res = await axios.post(
        API + "accounting/uservalidation/",
        { email: newAccountForm.email.trim() },
        { headers: authHeader() }
      );
      if (res.data && res.data.success === 0) {
        setNewAccountErrors((prev) => ({
          ...prev,
          email: res.data.response || "This email is already registered",
        }));
        return;
      }
    } catch (error) {
      // Non-blocking network hiccup — allow through, backend will still
      // reject a duplicate email at activation time.
    } finally {
      setSavingNewAccount(false);
    }

    const record = {
      id: `pending-${Date.now()}`,
      companyName: newAccountForm.companyName.trim(),
      firstName: newAccountForm.firstName.trim(),
      lastName: newAccountForm.lastName.trim(),
      email: newAccountForm.email.trim(),
      phone: newAccountForm.phone.trim(),
      createdAt: new Date().toISOString(),
    };
    const next = [...pendingAccounts, record];
    setPendingAccounts(next);
    savePending(next);
    notify(toast.success, "Account added. Click Activate to set up their subscription.");
    setShowNewAccountForm(false);
    resetNewAccountForm();
  };

  // ── new sub-company form ───────────────────────────────────────────────
  const openSubCompanyForm = () => {
    setSubCompanyForm(emptySubCompanyForm());
    setSubCompanyErrors({});
    setShowSubCompanyForm(true);
    if (plans.length === 0) {
      subcompanyService
        .getPlans()
        .then((res) => {
          const list = (res.data && res.data.data) || [];
          setPlans(list);
          applyDefaultFreePlan(list);
        })
        .catch(() => setPlans([]));
    } else {
      applyDefaultFreePlan(plans);
    }
  };

  // A free plan has no amount to collect and no payment method to choose.
  const isFreePlan = (p) => !!p && (p.is_free_plan || Number(p.per_user_amount) === 0);

  // New accounts start on the free plan; choosing it clears the amount and
  // resets the payment method (both are disabled while a free plan is selected).
  const applyDefaultFreePlan = (planList) => {
    const free = planList.find(isFreePlan);
    if (free) {
      setSubCompanyForm((prev) => ({
        ...prev,
        plan: free.plan_name,
        noOfUsers: 1,
        totalAmount: "",
        subscriptionType: "CASH",
      }));
    }
  };

  const selectedSubPlan = plans.find((p) => p.plan_name === subCompanyForm.plan);
  const isSubPlanFree = isFreePlan(selectedSubPlan);
  const suggestedSubAmount = selectedSubPlan
    ? (Number(selectedSubPlan.per_user_amount) || 0) * (Number(subCompanyForm.noOfUsers) || 0)
    : "";

  const handleSubCompanyFieldChange = (field) => (e) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setSubCompanyForm((prev) => ({ ...prev, [field]: value }));
    if (subCompanyErrors[field]) {
      setSubCompanyErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const handleSubCompanyPlanChange = (e) => {
    handleSubCompanyFieldChange("plan")(e);
    if (isFreePlan(plans.find((p) => p.plan_name === e.target.value))) {
      setSubCompanyForm((prev) => ({ ...prev, noOfUsers: 1, totalAmount: "", subscriptionType: "CASH" }));
    }
  };

  const validateSubCompany = () => {
    const errors = {};
    if (!subCompanyForm.companyName.trim()) errors.companyName = "Account name is required";
    if (!subCompanyForm.plan) errors.plan = "Select a plan";
    if (!(Number(subCompanyForm.noOfUsers) >= 1)) errors.noOfUsers = "At least 1 license";
    if (subCompanyForm.totalAmount !== "" && Number(subCompanyForm.totalAmount) < 0)
      errors.totalAmount = "Amount cannot be negative";
    return errors;
  };

  const handleCreateSubCompany = async (e) => {
    e.preventDefault();
    const errors = validateSubCompany();
    setSubCompanyErrors(errors);
    if (Object.keys(errors).length > 0) {
      notify(toast.error, "Please fix the highlighted fields");
      return;
    }

    setSavingSubCompany(true);
    try {
      const amount = isSubPlanFree
        ? 0
        : subCompanyForm.totalAmount === ""
        ? suggestedSubAmount || 0
        : Number(subCompanyForm.totalAmount);
      const res = await subcompanyService.createSubCompanyAccount({
        company_name: subCompanyForm.companyName.trim(),
        contact_name: subCompanyForm.contactName.trim(),
        phone_number: subCompanyForm.phone.trim(),
        address: subCompanyForm.address.trim(),
        city: subCompanyForm.city.trim(),
        plan: subCompanyForm.plan,
        no_of_users: Number(subCompanyForm.noOfUsers),
        total_amount: Math.round(amount),
        start_date: subCompanyForm.startDate,
        subscription_type: isSubPlanFree ? "CASH" : subCompanyForm.subscriptionType,
        bill_to_parent: subCompanyForm.billToParent,
      });
      if (res.data && res.data.success === 1) {
        notify(toast.success, `Account "${res.data.sub_company_name}" created.`);
        setShowSubCompanyForm(false);
        fetchAccounts();
      } else {
        notify(toast.error, (res.data && res.data.error) || "Failed to create Account");
      }
    } catch (err) {
      notify(
        toast.error,
        (err.response && err.response.data && err.response.data.error) || "Failed to create account"
      );
    } finally {
      setSavingSubCompany(false);
    }
  };

  const removePendingAccount = (id) => {
    const next = pendingAccounts.filter((a) => a.id !== id);
    setPendingAccounts(next);
    savePending(next);
  };

  // ── activation: the exact same checkout used from the Plans tab ─────────
  if (activatingAccount) {
    return (
      <div style={S.panel}>
        <ToastContainer />
        <SubscriberCheckout
          initialDetails={{
            firstName: activatingAccount.firstName,
            lastName: activatingAccount.lastName,
            email: activatingAccount.email,
            phone: activatingAccount.phone,
            companyName: activatingAccount.companyName,
          }}
          backLabel="Back to Accounts"
          onExit={() => setActivatingAccount(null)}
          onSuccess={() => {
            removePendingAccount(activatingAccount.id);
            fetchAccounts();
          }}
        />
      </div>
    );
  }

  // ── new account form: the same full-page swap pattern as the Party
  // "+ New Address" form (Party-Add-Address.js) — a plain header + card
  // with the form, replacing the list rather than a popup on top of it. ──
  if (showNewAccountForm) {
    return (
      <div className="container-fluid">
        <ToastContainer />
        <div className="header pb-0 mb-3">
          <h5>New Account</h5>
        </div>
        <div className="card" role="document">
          <div className="card-body">
            <form onSubmit={handleCreateAccountRecord}>
              <div className="form-group row">
                <div className="col-md-3">
                  <label className="col-form-label required">Company Name</label>
                  <input
                    className={`form-control${newAccountErrors.companyName ? " is-invalid" : ""}`}
                    value={newAccountForm.companyName}
                    onChange={handleNewAccountFieldChange("companyName")}
                    autoFocus
                  />
                  <div className="invalid-feedback">{newAccountErrors.companyName}</div>
                </div>
                <div className="col-md-3">
                  <label className="col-form-label required">First Name</label>
                  <input
                    className={`form-control${newAccountErrors.firstName ? " is-invalid" : ""}`}
                    value={newAccountForm.firstName}
                    onChange={handleNewAccountFieldChange("firstName")}
                  />
                  <div className="invalid-feedback">{newAccountErrors.firstName}</div>
                </div>
                <div className="col-md-3">
                  <label className="col-form-label required">Last Name</label>
                  <input
                    className={`form-control${newAccountErrors.lastName ? " is-invalid" : ""}`}
                    value={newAccountForm.lastName}
                    onChange={handleNewAccountFieldChange("lastName")}
                  />
                  <div className="invalid-feedback">{newAccountErrors.lastName}</div>
                </div>
              </div>
              <div className="form-group row">
                <div className="col-md-3">
                  <label className="col-form-label required">Email</label>
                  <input
                    type="email"
                    className={`form-control${newAccountErrors.email ? " is-invalid" : ""}`}
                    value={newAccountForm.email}
                    onChange={handleNewAccountFieldChange("email")}
                  />
                  <div className="invalid-feedback">{newAccountErrors.email}</div>
                </div>
                <div className="col-md-3">
                  <label className="col-form-label">Phone</label>
                  <input
                    className="form-control"
                    value={newAccountForm.phone}
                    onChange={handleNewAccountFieldChange("phone")}
                  />
                </div>
              </div>

              <div className="text-left py-3">
                <button
                  type="button"
                  className="btn btn-secondary btn-md"
                  onClick={() => setShowNewAccountForm(false)}
                >
                  Cancel
                </button>
                &nbsp;&nbsp;
                <button
                  type="submit"
                  disabled={savingNewAccount}
                  className="border-0 btn btn-primary btn-gradient-primary btn-md"
                >
                  {savingNewAccount ? "Checking..." : "Create Account"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // ── new sub-company form (same full-page swap pattern as New Account) ──
  if (showSubCompanyForm) {
    return (
      <div className="container-fluid">
        <ToastContainer />
        <div className="header pb-0 mb-3">
          <h5>New Account</h5>
          <p style={S.panelDesc}>
            No email or login is needed. You open it from here with Switch, and it keeps its own
            subscription.
          </p>
        </div>
        <div className="card" role="document">
          <div className="card-body">
            <form onSubmit={handleCreateSubCompany}>
              <div className="form-group row">
                <div className="col-md-3">
                  {/* <label className="col-form-label required">Company Name</label> */}
                  <label className="col-form-label required">Account Name</label>
                  <input
                    className={`form-control${subCompanyErrors.companyName ? " is-invalid" : ""}`}
                    value={subCompanyForm.companyName}
                    onChange={handleSubCompanyFieldChange("companyName")}
                    autoFocus
                  />
                  <div className="invalid-feedback">{subCompanyErrors.companyName}</div>
                </div>
                <div className="col-md-3">
                  <label className="col-form-label">Contact Name</label>
                  <input
                    className="form-control"
                    value={subCompanyForm.contactName}
                    onChange={handleSubCompanyFieldChange("contactName")}
                  />
                </div>
                <div className="col-md-3">
                  <label className="col-form-label">Phone</label>
                  <input
                    className="form-control"
                    value={subCompanyForm.phone}
                    onChange={handleSubCompanyFieldChange("phone")}
                  />
                </div>
              </div>
              <div className="form-group row">
                <div className="col-md-3">
                  <label className="col-form-label">Address</label>
                  <input
                    className="form-control"
                    value={subCompanyForm.address}
                    onChange={handleSubCompanyFieldChange("address")}
                  />
                </div>
                <div className="col-md-3">
                  <label className="col-form-label">City</label>
                  <input
                    className="form-control"
                    value={subCompanyForm.city}
                    onChange={handleSubCompanyFieldChange("city")}
                  />
                </div>
              </div>

              <h6 className="pt-3">Subscription</h6>
              <div className="form-group row">
                <div className="col-md-3">
                  <label className="col-form-label required">Plan</label>
                  <select
                    className={`form-control${subCompanyErrors.plan ? " is-invalid" : ""}`}
                    value={subCompanyForm.plan}
                    onChange={handleSubCompanyPlanChange}
                  >
                    <option value="">Select plan</option>
                    {plans.map((p) => (
                      <option key={p.id} value={p.plan_name}>
                        {p.plan_name}
                      </option>
                    ))}
                  </select>
                  <div className="invalid-feedback">{subCompanyErrors.plan}</div>
                </div>
                <div className="col-md-3">
                  <label className="col-form-label required">Licenses</label>
                  <input
                    type="number"
                    min="1"
                    className={`form-control${subCompanyErrors.noOfUsers ? " is-invalid" : ""}`}
                    value={isSubPlanFree ? 1 : subCompanyForm.noOfUsers}
                    disabled={isSubPlanFree}
                    onChange={handleSubCompanyFieldChange("noOfUsers")}
                  />
                  <div className="invalid-feedback">{subCompanyErrors.noOfUsers}</div>
                </div>
                <div className="col-md-3">
                  <label className="col-form-label">Amount</label>
                  <input
                    type="number"
                    min="0"
                    className={`form-control${subCompanyErrors.totalAmount ? " is-invalid" : ""}`}
                    value={isSubPlanFree ? 0 : subCompanyForm.totalAmount}
                    placeholder={suggestedSubAmount !== "" ? String(suggestedSubAmount) : ""}
                    disabled={isSubPlanFree}
                    onChange={handleSubCompanyFieldChange("totalAmount")}
                  />
                  <div className="invalid-feedback">{subCompanyErrors.totalAmount}</div>
                </div>
              </div>
              <div className="form-group row">
                <div className="col-md-3">
                  <label className="col-form-label">Start Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={subCompanyForm.startDate}
                    onChange={handleSubCompanyFieldChange("startDate")}
                  />
                </div>
                <div className="col-md-3">
                  <label className="col-form-label">Payment Method</label>
                  <select
                    className="form-control"
                    value={subCompanyForm.subscriptionType}
                    disabled={isSubPlanFree}
                    onChange={handleSubCompanyFieldChange("subscriptionType")}
                  >
                    <option value="CASH">Cash</option>
                    <option value="CHECK">Check</option>
                    <option value="ACH">ACH</option>
                    <option value="WIRE">Wire</option>
                    <option value="BANK">Bank</option>
                  </select>
                </div>
                <div className="col-md-3 d-flex align-items-end">
                  <div className="form-check mb-2">
                    <input
                      id="subCompanyBillToParent"
                      type="checkbox"
                      className="form-check-input"
                      checked={subCompanyForm.billToParent}
                      onChange={handleSubCompanyFieldChange("billToParent")}
                    />
                    <label className="form-check-label" htmlFor="subCompanyBillToParent">
                      Bill to this account
                    </label>
                  </div>
                </div>
              </div>

              <div className="text-left py-3">
                <button
                  type="button"
                  className="btn btn-secondary btn-md"
                  onClick={() => setShowSubCompanyForm(false)}
                >
                  Cancel
                </button>
                &nbsp;&nbsp;
                <button
                  type="submit"
                  disabled={savingSubCompany}
                  className="border-0 btn btn-primary btn-gradient-primary btn-md"
                >
                  {savingSubCompany ? "Creating..." : "Create Account"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // ── main list view ────────────────────────────────────────────────────────
  return (
    <div>
      <ToastContainer />

      <div className="d-flex align-items-center justify-content-between flex-wrap pb-3 mb-3 ">
        <div>
          <h2 style={S.panelTitle}>Accounts</h2>
          <p style={S.panelDesc}>Create new or manage existing accounts.</p>
        </div>
        <div className="d-flex flex-wrap" style={{ gap: 8 }}>
          <button
            type="button"
            // className="btn btn-outline-primary mb-0"
            className="btn btn-primary mb-0"
            onClick={openSubCompanyForm}
          >
            + New Account
          </button>
          {/* <button
            type="button"
            className="btn btn-primary mb-0"
            onClick={() => {
              resetNewAccountForm();
              setShowNewAccountForm(true);
            }}
          >
            + New Account
          </button> */}
        </div>
      </div>

      {pendingAccounts.length > 0 && (
        <div className="pt-4">
          <h5>{t("Pending Activation")}</h5>

          <MaterialTable
            style={materialTableChrome}
            columns={[
              {
                title: "Company",
                field: "companyName",
                headerStyle: tableHeadCellStyle,
                cellStyle: tableBodyCellStyle,
              },
              {
                title: "Contact",
                field: "firstName",
                headerStyle: tableHeadCellStyle,
                cellStyle: tableBodyCellStyle,
                render: (rowData) => `${rowData.firstName} ${rowData.lastName}`,
              },
              {
                title: "Email",
                field: "email",
                headerStyle: tableHeadCellStyle,
                cellStyle: tableBodyCellStyle,
              },
              {
                title: "Status",
                field: "id",
                headerStyle: tableHeadCellStyle,
                cellStyle: tableBodyCellStyle,
                render: () => <span style={{ ...S.badge, ...S.badgeWarning }}>Pending</span>,
              },
              {
                title: "Actions",
                field: "",
                headerStyle: tableHeadCellStyle,
                cellStyle: tableBodyCellStyle,
                sorting: false,
                render: (rowData) => (
                  <div style={S.rowActionsCell}>
                    <button
                      type="button"
                      style={S.tableActionBtn}
                      onClick={() => setActivatingAccount(rowData)}
                    >
                      Activate
                    </button>
                    <button
                      type="button"
                      style={S.tableActionBtnMuted}
                      onClick={() => removePendingAccount(rowData.id)}
                    >
                      Remove
                    </button>
                  </div>
                ),
              },
            ]}
            data={pendingAccounts}
            options={materialTableOptionsBase}
          />
        </div>
      )}

      <div className="pt-4">
        <h5>{t("Active Accounts")}</h5>

        <MaterialTable
          style={materialTableChrome}
          columns={[
            {
              title: "Account Name",
              field: "company_name",
              headerStyle: tableHeadCellStyle,
              cellStyle: tableBodyCellStyle,
            },
            {
              title: "Owner",
              field: "contact_name",
              headerStyle: tableHeadCellStyle,
              cellStyle: tableBodyCellStyle,
            },
            {
              title: "Currency",
              field: "currency",
              headerStyle: tableHeadCellStyle,
              cellStyle: tableBodyCellStyle,
            },
            {
              title: "Plan",
              field: "plan",
              headerStyle: tableHeadCellStyle,
              cellStyle: tableBodyCellStyle,
            },
            {
              title: "Status",
              field: "relationship_type",
              headerStyle: tableHeadCellStyle,
              cellStyle: tableBodyCellStyle,
              render: (rowData) => {
                const isCurrent = rowData.relationship_type === "self";
                return (
                  <>
                    <span style={{ ...S.badge, ...(isCurrent ? S.badgePrimary : S.badgeSuccess) }}>
                      {isCurrent ? "Current" : "Active"}
                    </span>
                    {rowData.is_sub_company && (
                      <span
                        style={{ ...S.badge, ...S.badgeMuted, marginLeft: 6 }}
                        title="No login of its own. Open it with Switch."
                      >
                        
                      </span>
                    )}
                  </>
                );
              },
            },
            {
              title: "Actions",
              field: "",
              headerStyle: tableHeadCellStyle,
              cellStyle: tableBodyCellStyle,
              sorting: false,
              render: (rowData) => {
                const isCurrent = rowData.relationship_type === "self";
                const isSwitching = switchingId === rowData.company_id;
                return (
                  <div style={S.rowActionsCell}>
                    {isCurrent && (
                      <span
                        className="cursor-pointer"
                        title="Edit Profile"
                        onClick={() => navigate("/profile")}
                      >
                        <img
                          src={process.env.PUBLIC_URL + "/static/images/ic-edit.svg"}
                          alt="Edit Profile"
                        />
                      </span>
                    )}
                    {!isCurrent && (
                      <span
                        className="cursor-pointer"
                        title={isSwitching ? "Switching..." : "Switch"}
                        style={{
                          opacity: isSwitching ? 0.5 : 1,
                          pointerEvents: isSwitching ? "none" : "auto",
                          color: "#245C7F",
                          fontSize: 16,
                          marginRight: 16,
                        }}
                        onClick={() => handleSwitchCompany(rowData.company_id)}
                      >
                        <i className="fa-solid fa-right-left" />
                      </span>
                    )}
                    {!isCurrent && (
                      <span
                        className="cursor-pointer"
                        title="Archive"
                        style={{ color: "#8392AB", fontSize: 16 }}
                        onClick={() => handleArchive(rowData)}
                      >
                        <i className="fa-solid fa-box-archive" />
                      </span>
                    )}
                  </div>
                );
              },
            },
          ]}
          data={accounts}
          isLoading={accountsLoading}
          detailPanel={(rowData) => <AccountDetailPanel account={rowData} />}
          options={{ ...materialTableOptionsBase, detailPanelType: "multiple" }}
        />

      </div>

      <div className="pt-4">
        <h5>{t("Archived Accounts")}</h5>
        <p style={S.sectionSubheading}>(data is removed 30 days after archival)</p>

        <MaterialTable
          style={{
            backgroundColor: "var(--bs-table-bg)",
            borderBottomWidth: "1px",
            boxShadow:
              "inset 0 0 0 9999px var(--bs-table-accent-bg)",
          }}
          columns={[
            {
              title: "Account Name",
              field: "company_name",
              headerStyle: tableHeadCellStyle,
              cellStyle: tableBodyCellStyle,
            },
            {
              title: "Owner",
              field: "contact_name",
              headerStyle: tableHeadCellStyle,
              cellStyle: tableBodyCellStyle,
            },
            {
              title: "Date Archived",
              field: "archived_at",
              headerStyle: tableHeadCellStyle,
              cellStyle: tableBodyCellStyle,
              render: (rowData) => fmtDate(rowData.archived_at),
            },
            {
              title: "Plan",
              field: "plan",
              headerStyle: tableHeadCellStyle,
              cellStyle: tableBodyCellStyle,
            },
            {
              title: "Status",
              field: "",
              headerStyle: tableHeadCellStyle,
              cellStyle: tableBodyCellStyle,
              render: () => <span style={{ ...S.badge, ...S.badgeMuted }}>Archived</span>,
            },
            {
              title: "Actions",
              field: "",
              headerStyle: tableHeadCellStyle,
              cellStyle: tableBodyCellStyle,
              sorting: false,
              render: (rowData) => (
                <div style={S.rowActionsCell}>
                  <span
                    className="cursor-pointer"
                    title="Reinstate"
                    style={{ color: "#245C7F", fontSize: 16 }}
                    onClick={() => handleReinstate(rowData)}
                  >
                    <i className="fa-solid fa-rotate-left" />
                  </span>
                </div>
              ),
            },
          ]}
          data={archivedAccounts}
          isLoading={archivedLoading}
          detailPanel={(rowData) => <AccountDetailPanel account={rowData} />}
          options={{
            headerStyle: {
              backgroundColor: "white",
              color: "#245C7F",
              fontSize: 11,
            },
            rowStyle: (rowData, index) =>
              index % 2 ? { backgroundColor: "#fff" } : {},
            sorting: true,
            search: false,
            toolbar: false,
            paging: true,
            pageSize: 10,
            emptyRowsWhenPaging: false,
            pageSizeOptions: [10, 20, 25, 50],
            paginationType: "stepped",
            showFirstLastPageButtons: false,
          }}
        />
      </div>
    </div>

  );
}

export default AccountsPage;
